import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const TIERS = [
  {
    id: "starter",
    name: "Starter Clinic",
    monthly: 150,
    annual: 1800,
    firstYear: 1650,
    desc: "For solo practices and small teams up to 5 sonographers.",
    features: ["Up to 5 sonographers", "Daily pain tracking", "Stretching library", "Personal trend reports", "Email support"],
    highlight: false,
  },
  {
    id: "professional",
    name: "Professional Clinic",
    monthly: 500,
    annual: 6000,
    firstYear: 5500,
    desc: "For multi-staff clinics that need manager analytics and reporting.",
    features: ["Up to 25 sonographers", "Everything in Starter", "Clinic analytics dashboard", "Anonymized hotspots", "Wellness Index badge", "Priority support"],
    highlight: true,
  },
];

const ENTERPRISE = {
  id: "enterprise",
  name: "Enterprise Multi-Location",
  desc: "For health systems, networks and research partners.",
  features: ["Unlimited sonographers", "Multi-clinic rollup", "Research data exports", "SSO + audit logs", "Dedicated CSM", "Custom SLAs"],
};

export default function Pricing() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [sub, setSub] = useState(null);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!user || user.role !== "clinic_manager") return;
    (async () => {
      try { const r = await api.get("/billing/subscription"); setSub(r.data); }
      catch { /* ignore */ }
    })();
  }, [user]);

  const subscribe = async (planId) => {
    if (!user) { nav(`/register?next=/pricing`); return; }
    if (user.role !== "clinic_manager") {
      setErr("Only clinic managers can purchase a subscription. Sign up as a clinic manager or contact your admin.");
      return;
    }
    setErr("");
    setBusy(planId);
    try {
      const { data } = await api.post("/billing/checkout", {
        plan: planId,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
      setBusy("");
    }
  };

  const isActive = (planId) => sub?.status === "active" && sub?.plan === planId;

  return (
    <div className="container-x py-20">
      <div className="text-center max-w-2xl mx-auto">
        <span className="pill">Pricing</span>
        <h1 className="mt-4 text-4xl sm:text-5xl font-black tracking-tight">Simple pricing. Serious outcomes.</h1>
        <p className="mt-5 text-lg text-zinc-600">Pay yearly, billed once. <span className="font-semibold">Your first month is on us</span> — first-year invoice covers 11 months. Cancel anytime.</p>
      </div>

      {err && <div className="mt-6 max-w-xl mx-auto card-elev text-red-600 text-sm" data-testid="pricing-error">{err}</div>}

      <div className="mt-14 grid md:grid-cols-3 gap-6">
        {TIERS.map((t) => (
          <div
            key={t.id}
            data-testid={`pricing-${t.id}`}
            className={`rounded-2xl p-8 border ${t.highlight ? "border-[#d4af37] shadow-xl ring-1 ring-[#d4af37]/30" : "border-zinc-200"} bg-white flex flex-col`}
          >
            {t.highlight && <span className="pill mb-4">Most popular</span>}
            <h3 className="font-bold text-xl">{t.name}</h3>
            <div className="mt-4 flex items-baseline gap-2">
              <span className="text-4xl font-extrabold">${t.monthly}</span>
              <span className="text-zinc-500 text-sm">/clinic / month</span>
            </div>
            <div className="text-sm text-zinc-500 mt-1">
              Billed yearly: <span className="font-semibold text-zinc-900">${t.annual.toLocaleString()}</span> · First year <span className="font-semibold" style={{ color: "var(--gold-hover)" }}>${t.firstYear.toLocaleString()}</span>
            </div>
            <p className="mt-3 text-zinc-600 text-sm leading-relaxed">{t.desc}</p>
            <button
              onClick={() => subscribe(t.id)}
              disabled={busy === t.id || isActive(t.id)}
              className={`mt-6 w-full text-center ${t.highlight ? "btn-gold" : "btn-outline"}`}
              data-testid={`pricing-cta-${t.id}`}
            >
              {isActive(t.id) ? "Current plan" : busy === t.id ? "Redirecting\u2026" : "Start free month"}
            </button>
            <ul className="mt-7 space-y-3 text-sm">
              {t.features.map((f) => (
                <li key={f} className="flex items-start gap-2">
                  <Check className="w-4 h-4 mt-0.5" style={{ color: "var(--gold)" }} />
                  <span className="text-zinc-700">{f}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}

        <div className="rounded-2xl p-8 border border-zinc-200 bg-white flex flex-col" data-testid={`pricing-${ENTERPRISE.id}`}>
          <h3 className="font-bold text-xl">{ENTERPRISE.name}</h3>
          <div className="mt-4 flex items-baseline gap-1">
            <span className="text-4xl font-extrabold">Custom</span>
          </div>
          <p className="mt-3 text-zinc-600 text-sm leading-relaxed">{ENTERPRISE.desc}</p>
          <Link to="/contact" className="mt-6 btn-outline text-center" data-testid={`pricing-cta-${ENTERPRISE.id}`}>Contact sales</Link>
          <ul className="mt-7 space-y-3 text-sm">
            {ENTERPRISE.features.map((f) => (
              <li key={f} className="flex items-start gap-2">
                <Check className="w-4 h-4 mt-0.5" style={{ color: "var(--gold)" }} />
                <span className="text-zinc-700">{f}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <p className="mt-10 text-center text-xs text-zinc-500 max-w-2xl mx-auto">
        Pricing is per clinic, not per sonographer. Renewals are charged for a full 12-month term at the listed annual rate. Taxes may apply. Powered by Stripe.
      </p>
    </div>
  );
}
